import * as React from 'react';

import { useQuery } from '@apollo/client';

import { useLocation } from 'js/lib/useRouter';

import CampaignModulesDataContext from 'bundles/campaigns/context/CampaignModulesDataContext';
import useIsPreview from 'bundles/campaigns/hooks/useIsPreview';
import GetCampaignModulesForLohpQuery from 'bundles/campaigns/queries/GetCampaignModulesForLohpQuery.graphql';
import type {
  GetCampaignModulesForLohpQuery as GetCampaignModulesForLohpQueryType,
  GetCampaignModulesForLohpQueryVariables,
} from 'bundles/campaigns/queries/__generated__/GetCampaignModulesForLohpQuery';
import { SentryErrorType, captureExceptionInSentry } from 'bundles/campaigns/utils/sentryUtil';

const CampaignModulesDataProviderForLohp = ({ children }: React.PropsWithChildren) => {
  const { query } = useLocation();
  const isPreview = useIsPreview();
  const campaignId = query.campaignId ?? null;

  const { data, loading } = useQuery<GetCampaignModulesForLohpQueryType, GetCampaignModulesForLohpQueryVariables>(
    GetCampaignModulesForLohpQuery,
    {
      variables: {
        campaignModulesVariables: {
          useContentfulPreview: isPreview,
          forceUseCampaignId: campaignId,
        },
      },
      context: {
        clientName: 'gatewayGql',
      },
      errorPolicy: 'all',
      ssr: true,
      onError: (error) => {
        captureExceptionInSentry({
          error,
          extra: {
            message: 'Error fetching campaign modules on LOHP',
            campaignId,
            isPreview,
          },
          errorType: SentryErrorType.GRAPHQL_ERROR,
        });
      },
    }
  );

  let heroModuleData;
  let collectionsModuleData;
  let cannedCollectionModuleData;

  const modules = data?.Campaign?.getCampaignModulesForLohp;
  if (modules?.hero?.__typename === 'Campaign_HeroModule') {
    heroModuleData = modules.hero;
  }
  if (modules?.spotlightCollections?.__typename === 'Campaign_SpotlightCollectionsModule') {
    collectionsModuleData = modules.spotlightCollections;
  }
  if (modules?.cannedCollection?.__typename === 'Campaign_CannedCollectionModule') {
    cannedCollectionModuleData = modules.cannedCollection;
  }

  return (
    <CampaignModulesDataContext.Provider
      value={{
        loading,
        campaignData: {
          heroModule: heroModuleData,
          collectionsModule: collectionsModuleData,
          cannedCollectionModule: cannedCollectionModuleData,
        },
      }}
    >
      {children}
    </CampaignModulesDataContext.Provider>
  );
};

export default CampaignModulesDataProviderForLohp;
